// frontend/src/pages/admin/LoginHistoryPage.tsx
//
// Phase A (2026-06-25) — admin-only login/device history. Answers
// "show me <rep>'s logins with devices and times." Tenant-scoped + admin/owner
// only on the backend; history starts empty (no backfill) and fills going forward.
import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { formatDate } from '../../utils/formatters';

interface LoginEvent {
  id: string;
  userId: string;
  /** B45: who signed in (resolved by the API). */
  userName?: string | null;
  userEmail?: string | null;
  userRole?: string | null;
  ipAddress: string | null;
  deviceId: string | null;
  deviceModel: string | null;
  osVersion: string | null;
  appVersionName: string | null;
  appVersionCode: number | null;
  loginPath: string;
  createdAt: string;
}

export function LoginHistoryPage() {
  const [events, setEvents] = useState<LoginEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // B45 (2026-10-12): filter by a person's name or email, not a raw user id.
  const [q, setQ] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ limit: '200' });
    if (q.trim()) params.set('q', q.trim());
    api
      .get(`/admin/login-events?${params}`)
      .then((res) => setEvents(res.data.data.events))
      .catch((err) =>
        setError(
          err?.response?.status === 403
            ? 'You don’t have access to login history.'
            : 'Couldn’t load login history. Try again.',
        ),
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Login &amp; Device History</h1>
          <p className="text-sm text-gray-500">
            Who logged in, from what device, IP and time — newest first
          </p>
        </div>
        <input
          type="text"
          placeholder="Filter by name or email…"
          className="w-56 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500">No logins recorded yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Who</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Device</th>
                <th className="px-4 py-3">OS</th>
                <th className="px-4 py-3">App</th>
                <th className="px-4 py-3">Path</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-3 whitespace-nowrap">{formatDate(e.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{e.userName ?? 'Unknown user'}</div>
                    {e.userEmail && <div className="text-xs text-gray-500">{e.userEmail}</div>}
                  </td>
                  <td className="px-4 py-3">{e.ipAddress ?? '—'}</td>
                  <td className="px-4 py-3">{e.deviceModel ?? '—'}</td>
                  <td className="px-4 py-3">{e.osVersion ?? '—'}</td>
                  <td className="px-4 py-3">
                    {e.appVersionName
                      ? `${e.appVersionName}${e.appVersionCode != null ? `+${e.appVersionCode}` : ''}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">{e.loginPath}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
